import { trackAwsRumError, trackAwsRumEvent } from './awsTracking';
import { logEvent as logGA4Event, logPageView as logGA4PageView } from './ga4Config';
import ReactGA from 'react-ga4';

/**
 * A convenience helper that acts as an adapter to log errors to console and AWS RUM
 * (or wherever tracking is configured).
 *
 * @param context Additional contextual information about where the error occurred.
 * @param error An error object or any value representing the error.
 */
export function logError(context: string, error: unknown): void {
  if (error instanceof Error) {
    trackAwsRumError(error);
  }

  // Also explicitly tracking errors as events, for additional context.
  trackAwsRumEvent('proprio.error', { context: context, error: String(error) });
  console.error(context);
};

/**
 * A convenience helper that acts as an adapter to log arbitrary events to AWS RUM.
 * (or wherever tracking is configured).
 *
 * @param eventData An object containing event data to log.
 */
export function logEvent(eventData: object): void {
  try {
    trackAwsRumEvent('proprio.event', eventData);
    console.log('Event logged:', eventData);
  } catch (error) {
    console.error('Error logging event to AWS RUM:', error);
  }
};

/**
 * Traces page views to configured tracking services.
 *
 * @param path The path of the page being viewed.
 */
export function logPageView(path: string): void {
  try {
    logGA4PageView(path);

    // Track page views as explicit RUM event in addition to default RUM navigation tracking.
    trackAwsRumEvent('proprio.page_view', { path: path });
    console.log('Page view logged:', path);
  } catch (error) {
    console.error('Error logging page view to tracking services:', error);
  }
}

/**
 * Logs user actions to tracking configured tracking services
 *
 * @param category A trace category
 * @param action   A user action
 * @param label    A label providing additional context (eg: Selected item name).
 */
export function logTrace(category: string, action: string, label: string): void {
  try {
    logGA4Event(category, action, label);

    const traceData = { category, action, label };
    trackAwsRumEvent('proprio.trace', traceData);
    console.log('Trace logged:', traceData);
  } catch (error) {
    console.error('Error logging trace to tracking services:', error);
  }
}

/**
 * Traces login event and performs any necessary setup for tracking logged-in user.
 *
 * @param userId The user ID of the logged-in user.
 */
export function onLogin(userId: string): void {
  logTrace('UserProfile', 'proprio_login', userId);
  ReactGA.set({ userId: userId });
};

/**
 * Traces logout event and performs any necessary teardown for tracking logged-out user.
 */
export function onLogout(): void {
  logTrace('UserProfile', 'proprio_logout', '');
  ReactGA.set({ userId: null });
};

/**
 * Traces session expiration event (called when 401 triggers automatic logout).
 */
export function onSessionExpired(): void {
  logTrace('UserProfile', 'proprio_session_expired', '');
};

